/**
 * ICONTACTIC pure logic (no DOM). Shared by the storefront components and the Node test-suite.
 * Mirrors snippets/icontactic-gate.liquid (server-side render decisions).
 *
 * Contracts: docs/06-product-card-contract.md · docs/06a-pdp-component-contract.md ·
 *            docs/05b-variant-and-configurator-architecture.md
 */

export const ELIGIBLE_STATUSES = ['LIKELY_ELIGIBLE', 'APPROVED_IN_WRITING'];
export const CONFIGURATOR_ORDER = ['hand', 'manufacturer', 'model', 'holster_color'];

/**
 * Server-equivalent product gate. Used by tests and by the dev fixture harness.
 * @param {object} p fixture-shaped product: { purchase_mode, readiness_status, supplier_verification_status,
 *   compliance_profile: { regulated_function, decisions: [] } | null, safety_notice: boolean,
 *   variants: [{ available, price }], selected_variant_index? }
 * @param {object} [opts] { scope: 'all' | configuration scope, now: epoch seconds }
 * @returns {{ action: 'add'|'choose'|'configure'|'view', purchasable: boolean, reason: string, eligible: boolean, verified: boolean, variantAvailable: boolean }}
 */
export function evaluateProductGate(p, opts = {}) {
  const scope = opts.scope || 'all';
  const now = opts.now ?? Math.floor(Date.now() / 1000);
  const purchaseMode = p.purchase_mode || 'standard';
  const readiness = p.readiness_status || 'SOURCE_INTAKE';
  const verification = p.supplier_verification_status || 'open';
  const profile = p.compliance_profile || null;
  const regulated = !!(profile && profile.regulated_function === true);
  const hasSafety = !!p.safety_notice;

  let eligible = false;
  if (profile && Array.isArray(profile.decisions)) {
    for (const d of profile.decisions) {
      if (d.provider_or_channel !== 'shopify_online_store') continue;
      const dScope = d.configuration_scope || 'all';
      if (dScope !== 'all' && dScope !== scope) continue;
      if (!ELIGIBLE_STATUSES.includes(d.status)) continue;
      if (d.written_approval_required === true && d.status !== 'APPROVED_IN_WRITING') continue;
      if (d.expiration_or_review_date) {
        const exp = Math.floor(new Date(d.expiration_or_review_date).getTime() / 1000);
        if (exp < now) continue;
      }
      eligible = true;
    }
  }

  const active = readiness === 'ACTIVE_APPROVED';
  const verified = verification === 'verified';
  const variants = Array.isArray(p.variants) ? p.variants : [];
  const selected = variants[p.selected_variant_index ?? 0] || variants.find((v) => v.available) || variants[0] || null;
  const variantAvailable = !!(selected && selected.available);
  const priceOk = !!(selected && Number(selected.price) > 0);

  let purchasable = true;
  let reason = '';
  if (!eligible) { purchasable = false; reason = 'not_eligible'; }
  else if (!active) { purchasable = false; reason = 'not_active'; }
  else if (!verified) { purchasable = false; reason = 'not_verified'; }
  else if (regulated && !hasSafety) { purchasable = false; reason = 'regulated_without_notice'; }
  else if (!priceOk) { purchasable = false; reason = 'price_missing'; }

  let action = 'view';
  if (purchasable) {
    if (purchaseMode === 'configurator') action = 'configure';
    else if (purchaseMode === 'custom_measure') action = 'choose';
    else if (purchaseMode === 'acknowledgment') { action = 'view'; reason = 'acknowledgment_required'; }
    else if (variants.length > 1) action = 'choose';
    else if (!variantAvailable) { action = 'view'; reason = 'unavailable'; }
    else action = 'add';
  }
  return { action, purchasable, reason, eligible, verified, variantAvailable, purchaseMode, readiness, regulated, hasSafety };
}

/**
 * Live purchase gate (PDP). Combines the server verdict with live variant + component state.
 * @param {object} ctx { purchasable, reason, variantAvailable, components: [{id, kind, active, required, valid, message}] }
 * @returns {{ valid: boolean, reasons: {code: string, message?: string, componentId?: string}[] }}
 */
export function evaluatePurchaseGate(ctx) {
  const reasons = [];
  if (!ctx.purchasable) reasons.push({ code: ctx.reason || 'not_eligible' });
  if (!ctx.variantAvailable) reasons.push({ code: 'unavailable' });
  for (const c of ctx.components || []) {
    if (!c.active) continue;
    if (c.required && !c.valid) reasons.push({ code: c.code || `${c.kind}_required`, message: c.message, componentId: c.id });
  }
  return { valid: reasons.length === 0, reasons };
}

/** Supported records only (fail closed). */
export function supportedRecords(records) {
  return (records || []).filter((r) => r && r.support_status === 'supported');
}

function handMatches(recordHand, selectedHand) {
  if (!selectedHand) return true;
  return recordHand === 'both' || recordHand === selectedHand;
}

function recordMatchesUpTo(record, selection, depth) {
  for (let i = 0; i < depth; i++) {
    const key = CONFIGURATOR_ORDER[i];
    const value = selection[key];
    if (!value) return true; // an unselected earlier level does not constrain
    if (key === 'hand') { if (!handMatches(record.hand, value)) return false; }
    else if (record[key] !== value) return false;
  }
  return true;
}

/**
 * Cascading option values. Each level lists only values that exist in a supported record compatible with
 * the selections made at earlier levels. Unsupported combinations never appear.
 * @returns {Record<string, string[]>}
 */
export function cascadeOptions(records, selection = {}) {
  const supported = supportedRecords(records);
  const out = {};
  CONFIGURATOR_ORDER.forEach((key, depth) => {
    const values = new Set();
    for (const r of supported) {
      if (!recordMatchesUpTo(r, selection, depth)) continue;
      if (key === 'hand') {
        if (r.hand === 'both') { values.add('right'); values.add('left'); }
        else if (r.hand) values.add(r.hand);
      } else if (r[key]) values.add(r[key]);
    }
    out[key] = [...values];
  });
  return out;
}

/** Exact resolution of the selected combination to one supported record (or null). */
export function resolveRecord(records, selection) {
  if (!CONFIGURATOR_ORDER.every((k) => selection[k])) return null;
  const supported = supportedRecords(records);
  return (
    supported.find(
      (r) =>
        handMatches(r.hand, selection.hand) &&
        r.manufacturer === selection.manufacturer &&
        r.model === selection.model &&
        r.holster_color === selection.holster_color
    ) || null
  );
}

/**
 * Configurator state → validity + line-item properties.
 * `_holster_sku` is a FULFILLMENT INSTRUCTION only. It never deducts inventory (docs/05b, PILOT model).
 */
export function configuratorState(records, selection, propertyNames = {}) {
  const names = {
    hand: 'Holster hand',
    manufacturer: 'Firearm make',
    model: 'Firearm model',
    holster_color: 'Holster color',
    sku: '_holster_sku',
    ...propertyNames,
  };
  const complete = CONFIGURATOR_ORDER.every((k) => !!selection[k]);
  const record = complete ? resolveRecord(records, selection) : null;
  const valid = !!record;
  let code = '';
  if (!complete) code = 'configuration_required';
  else if (!record) code = 'configuration_unsupported';
  const properties = {};
  if (valid) {
    properties[names.hand] = selection.hand;
    properties[names.manufacturer] = selection.manufacturer;
    properties[names.model] = selection.model;
    properties[names.holster_color] = selection.holster_color;
    properties[names.sku] = record.holster_sku;
  }
  return { complete, record, valid, code, properties };
}

/**
 * Custom measurement validation. Integer > 0; optional min/max only when supplied (0/undefined = not set).
 * Never changes price (surcharges live on the variant).
 */
export function validateMeasurement(raw, { min = 0, max = 0 } = {}) {
  const str = String(raw ?? '').trim();
  if (str === '') return { valid: false, code: 'measurement_required', value: null };
  if (!/^\d+$/.test(str)) return { valid: false, code: 'measurement_invalid', value: null };
  const value = parseInt(str, 10);
  if (value <= 0) return { valid: false, code: 'measurement_invalid', value };
  if (min > 0 && value < min) return { valid: false, code: 'measurement_out_of_range', value };
  if (max > 0 && value > max) return { valid: false, code: 'measurement_out_of_range', value };
  return { valid: true, code: '', value };
}

/**
 * Merge the properties of all ACTIVE + VALID components into the [name, value] pairs sent to /cart/add.js.
 * Inactive components contribute nothing (their values were cleared).
 */
export function serializeProperties(components) {
  const out = [];
  for (const c of components || []) {
    if (!c.active || !c.valid || !c.properties) continue;
    for (const [k, v] of Object.entries(c.properties)) {
      if (v === undefined || v === null || v === '') continue;
      out.push([k, String(v)]);
    }
  }
  return out;
}

/** Whether a trigger (option name/value) is satisfied by the current selected options. */
export function triggerActive(trigger, options) {
  if (!trigger || !trigger.option) return true;
  const value = options ? options[trigger.option] : undefined;
  if (trigger.value === undefined || trigger.value === '') return value !== undefined;
  return String(value).trim().toLowerCase() === String(trigger.value).trim().toLowerCase();
}
